import {getSelectedKeysForPathname} from '@rdcomponents/app/routing/hooks/app-navigation-helper';
import {ConfigProvider, Menu} from 'antd';
import {MenuInfo} from 'rc-menu/lib/interface';
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import {OnboardingContext} from '../../provider/onboarding/onboarding-context';
import {useSystem} from '../../provider/system/system-hook';
import {UiContext} from '../../provider/ui/ui-context';
import {UiContextType} from '../../provider/ui/ui-context-type';
import {classNames} from '../../util/css';
import {useUrl} from '../../util/url-hook/url-hook';
import {useMenuNavigation} from '../app/routing/hooks/use-app-navigation';
import {LANGUAGE_MENU_KEY} from '../app/routing/routing-const';

import {MobileLanguageSelector} from './mobile-language-selector/mobile-language-selector';
import {makeHardReload} from './navigation-helper';
import {OnboardingButton} from './onboarding-button/onboarding-button';
import {SearchButton} from './search-button/search-button';
import * as styles from './navigation.scss';

type PropsType = {
    useHardReload?: boolean;
    isHeaderShown: boolean;
};

// eslint-disable-next-line max-statements
export function Navigation(props: PropsType): JSX.Element {
    const {useHardReload, isHeaderShown} = props;

    const [isMobileLanguageSelectorOpen, setIsMobileLanguageSelectorOpen] = useState(false);

    const {isLeftSideOpen, isMobileNavigationOpen, setIsMobileNavigationOpen} = useContext<UiContextType>(UiContext);
    const {screen} = useSystem();
    const {pathname} = useUrl();
    const {allMenuItems, visibleMenuItems} = useMenuNavigation();

    const selectedKeys = useMemo(() => getSelectedKeysForPathname(pathname, allMenuItems), [allMenuItems, pathname]);

    const {showOnboarding} = useContext(OnboardingContext);

    const {isDesktop, isMobile} = screen;
    const isInlineCollapsed = isDesktop && !isLeftSideOpen;
    const defaultOpenKeys = useRef(isLeftSideOpen ? getSelectedKeysForPathname(pathname, allMenuItems) : []);

    function onClickMenu(event: MenuInfo): void {
        if (event.key !== LANGUAGE_MENU_KEY) {
            setIsMobileNavigationOpen(!isMobileNavigationOpen);
        }

        if (useHardReload) {
            makeHardReload();
        }
    }

    const navRef = useRef<HTMLDivElement | null>(null);
    const [openKeys, setOpenKeys] = useState<Array<string>>(defaultOpenKeys.current);

    const previousOpenKeys = useRef<Array<string>>(defaultOpenKeys.current);

    const scrollToOpenedKey = useCallback((openedKey: string) => {
        setTimeout(() => {
            const selector = `.ant-menu-submenu-title[aria-controls*="${openedKey}"]`;
            const submenu = document.querySelector(selector) as HTMLElement | null;

            if (!submenu || !navRef.current) {
                return;
            }

            const container = navRef.current;

            container.scrollTo({
                top: submenu.offsetTop - container.offsetTop + Math.min(submenu.offsetHeight),
                behavior: 'smooth',
            });
        }, 300);
    }, []);

    useEffect(() => {
        const container = navRef.current;

        if ((isDesktop && !isLeftSideOpen) || !container) {
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            return () => {};
        }

        const timerId = window.setTimeout(() => {
            const leaf = container.querySelector('.ant-menu-item-selected');

            if (leaf instanceof HTMLElement) {
                leaf.scrollIntoView({behavior: 'smooth', block: 'nearest'});
            }
        }, 0);

        return () => window.clearTimeout(timerId);
    }, [isDesktop, isLeftSideOpen, pathname, selectedKeys]);

    function handleOpenChange(nextOpenKeys: Array<string>) {
        const newlyOpenedKey = nextOpenKeys.find((key) => !previousOpenKeys.current.includes(key));

        if (newlyOpenedKey) {
            scrollToOpenedKey(newlyOpenedKey);
        }

        previousOpenKeys.current = nextOpenKeys;
        setOpenKeys(nextOpenKeys);
    }

    const wrapperClassName = classNames(styles.navigation, {
        [styles.navigation__close]: !isLeftSideOpen,
        [styles.navigation__mobile]: !isDesktop,
        [styles.navigation__without_header]: !isHeaderShown,
    });

    const menuClassName = classNames(styles.navigation_menu, {
        [styles.navigation_menu_with_onboarding]: showOnboarding,
    });

    return (
        <div aria-label="navigation" className={wrapperClassName} ref={navRef}>
            <ConfigProvider theme={{components: {Menu: {activeBarBorderWidth: 0}}}}>
                <OnboardingButton inlineCollapsed={isInlineCollapsed} />
                <SearchButton inlineCollapsed={isInlineCollapsed} />
                <Menu
                    className={menuClassName}
                    inlineCollapsed={isInlineCollapsed}
                    items={visibleMenuItems}
                    mode="inline"
                    onClick={onClickMenu}
                    onOpenChange={handleOpenChange}
                    openKeys={openKeys}
                    selectedKeys={selectedKeys}
                />
                {isMobile && (
                    <MobileLanguageSelector
                        onClose={() => setIsMobileLanguageSelectorOpen(false)}
                        open={isMobileLanguageSelectorOpen}
                    />
                )}
            </ConfigProvider>
        </div>
    );
}
